import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, Check, Quote, Mic2, Clapperboard, Palette,
  Video, Rocket, Building2, ChevronRight,
} from "lucide-react";
import { FadeUp, MaskLine, SectionTag } from "../components/site/Reveal";
import { CreativeLeadForm } from "../components/site/CreativeLeadForm";
import {
  CREATIVE_ASSETS, CREATIVE_STAGES, CREATIVE_WHO, CREATIVE_BUILD,
  CREATIVE_BEFORE, CREATIVE_AFTER, CREATIVE_3G, CREATIVE_REVENUE_MAP,
  CREATIVE_IP_LIST, CREATIVE_OFFERS, CREATIVE_DIFFERENCE, CREATIVE_QUALIFY,
  TESTIMONIALS,
} from "../content";

const WHO_ICONS = [Mic2, Video, Palette, Clapperboard, Rocket, Building2];

const scrollToForm = (e) => {
  e.preventDefault();
  document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" });
};

const Hero = () => (
  <section data-testid="cg-hero" className="relative overflow-hidden pt-36 lg:pt-44">
    <div className="absolute -top-24 right-[-10%] h-[30rem] w-[30rem] rounded-full bg-gold/20 blur-[130px]" />
    <div className="absolute -left-40 top-1/2 h-[24rem] w-[24rem] rounded-full bg-gold/10 blur-[120px]" />
    <div className="relative mx-auto max-w-7xl px-6 pb-24 lg:px-10">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="mb-8 font-mono text-xs uppercase tracking-[0.35em] text-golddeep"
        data-testid="cg-hero-eyebrow"
      >
        CollabIgnite™ Creative Growth
      </motion.p>
      <h1 className="max-w-4xl font-serif text-5xl font-bold leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl">
        <MaskLine delay={0.35}>Your talent is the asset.</MaskLine>
        <MaskLine delay={0.5}>Now build the <span className="gold-text italic">business</span></MaskLine>
        <MaskLine delay={0.65}>around it.</MaskLine>
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.0 }}
        className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
        data-testid="cg-hero-subhead"
      >
        You created the talent. You built the audience. You developed the relationships
        and the work. Now build the infrastructure that turns those assets into
        sustainable revenue, strategic opportunities, and long-term growth. You stay
        the artist. The business gets built around you.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.2 }}
        className="mt-10 flex flex-wrap items-center gap-4"
      >
        <a href="#lead-form" onClick={scrollToForm} data-testid="cg-hero-cta" className="btn-gold">
          Build My Creative Business <ArrowRight size={16} />
        </a>
        <a
          href="#cg-system"
          onClick={(e) => { e.preventDefault(); document.getElementById("cg-system")?.scrollIntoView({ behavior: "smooth" }); }}
          data-testid="cg-hero-secondary"
          className="btn-ghost"
        >
          See How It Works
        </a>
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
          Clarity | Focus | Action
        </span>
      </motion.div>
    </div>
  </section>
);

const Problem = () => (
  <section data-testid="cg-problem" className="border-t border-gold/25 bg-cream">
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
      <FadeUp>
        <SectionTag>The Problem</SectionTag>
        <h2 className="mt-4 max-w-3xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Talent alone doesn't build a business.
        </h2>
      </FadeUp>
      <div className="mt-12 grid gap-10 lg:grid-cols-2">
        <FadeUp>
          <div className="space-y-4 font-serif text-xl font-medium leading-relaxed text-ink">
            <p>You can have talent and still struggle financially.</p>
            <p>You can have an audience and still have inconsistent income.</p>
            <p>You can have relationships and still miss opportunities.</p>
            <p>You can create valuable intellectual property and never fully monetize it.</p>
            <p>You can be busy every day and still not be building an enterprise.</p>
          </div>
          <p className="mt-8 max-w-lg text-base leading-relaxed text-slate-600">
            The problem is not your creativity. It is the business infrastructure behind it.
          </p>
        </FadeUp>
        <FadeUp delay={0.12}>
          <div className="border border-gold/40 bg-white p-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-golddeep">Sound familiar?</p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {[
                "Inconsistent revenue", "Underpricing", "No clear business model",
                "Opportunities without strategy", "Weak contracts and processes",
                "No sponsorship strategy", "Unorganized IP", "Limited monetization",
                "Founder dependency", "No scalable systems", "Difficulty accessing capital",
                "No path from audience to ownership",
              ].map((c) => (
                <span key={c} className="border border-slate-300 bg-paper px-3.5 py-1.5 text-xs text-slate-600">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
      <FadeUp delay={0.15}>
        <p className="mt-16 max-w-3xl font-serif text-2xl font-semibold leading-snug text-ink sm:text-3xl">
          You don't need to become less creative. You need a{" "}
          <span className="gold-text italic">stronger business</span> around your creativity.
        </p>
      </FadeUp>
    </div>
  </section>
);

const Assets = () => (
  <section data-testid="cg-assets" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <FadeUp>
      <SectionTag>The Opportunity</SectionTag>
      <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
        Turn what you already have into more.
      </h2>
      <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-600">
        You are sitting on five assets. Most creatives never organize them. That is where
        the work starts.
      </p>
    </FadeUp>
    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
      {CREATIVE_ASSETS.map((a, i) => (
        <FadeUp key={a.title} delay={i * 0.06}>
          <article
            data-testid={`cg-asset-${i}`}
            className="group h-full border border-gold/25 bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:border-golddeep/60"
          >
            <span className="font-mono text-xs text-golddeep/60">0{i + 1}</span>
            <h3 className="mt-4 font-serif text-xl font-semibold text-ink">{a.title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{a.body}</p>
          </article>
        </FadeUp>
      ))}
    </div>
    <FadeUp delay={0.2}>
      <p className="mt-10 font-serif text-xl italic text-ink">
        CollabIgnite™ helps organize these assets into one business strategy.
      </p>
    </FadeUp>
  </section>
);

const System = () => (
  <section id="cg-system" data-testid="cg-system" className="border-y border-gold/25 bg-cream">
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
      <FadeUp>
        <SectionTag>The Creative Growth System™</SectionTag>
        <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          From creative talent to creative enterprise.
        </h2>
      </FadeUp>
      <div className="mt-14 space-y-6">
        {CREATIVE_STAGES.map((s, i) => (
          <FadeUp key={s.num} delay={i * 0.05}>
            <article
              data-testid={`cg-stage-${s.num}`}
              className="group grid gap-6 border border-gold/25 bg-white p-8 transition-colors duration-500 hover:border-golddeep/60 md:grid-cols-12 md:items-center"
            >
              <span className="font-serif text-5xl font-bold text-gold/40 transition-colors duration-500 group-hover:text-gold md:col-span-2">
                {s.num}
              </span>
              <div className="md:col-span-4">
                <h3 className="font-serif text-2xl font-semibold text-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.body}</p>
              </div>
              <div className="flex flex-wrap gap-2 md:col-span-6">
                {s.items.map((item) => (
                  <span key={item} className="border border-gold/30 bg-paper px-3 py-1.5 text-xs text-slate-600">
                    {item}
                  </span>
                ))}
              </div>
            </article>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Who = () => (
  <section data-testid="cg-who" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <FadeUp>
      <SectionTag>Who This Is For</SectionTag>
      <h2 className="mt-4 max-w-3xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
        Built for creatives ready to build beyond the creative.
      </h2>
    </FadeUp>
    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {CREATIVE_WHO.map((w, i) => {
        const Icon = WHO_ICONS[i];
        return (
          <FadeUp key={w.title} delay={i * 0.06}>
            <article
              data-testid={`cg-who-${i}`}
              className="group h-full border border-gold/25 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-golddeep/60 hover:shadow-[0_20px_50px_-20px_rgba(140,109,31,0.35)]"
            >
              <Icon size={24} className="text-golddeep" />
              <h3 className="mt-5 font-serif text-xl font-semibold text-ink">{w.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{w.body}</p>
            </article>
          </FadeUp>
        );
      })}
    </div>
  </section>
);

const Build = () => (
  <section data-testid="cg-build" className="border-y border-gold/25 bg-cream">
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
      <FadeUp>
        <SectionTag>What Gets Built</SectionTag>
        <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Build more than a following.
        </h2>
      </FadeUp>
      <div className="mt-14 grid gap-px bg-gold/25 sm:grid-cols-2 lg:grid-cols-5">
        {CREATIVE_BUILD.map((b, i) => (
          <FadeUp key={b.title} delay={i * 0.04}>
            <article data-testid={`cg-build-${i}`} className="h-full bg-cream p-7 transition-colors duration-500 hover:bg-white">
              <h3 className="font-serif text-lg font-semibold text-ink">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.body}</p>
            </article>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Transformation = () => (
  <section data-testid="cg-transformation" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <FadeUp>
      <SectionTag>The Transformation</SectionTag>
      <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
        Same talent. Different trajectory.
      </h2>
    </FadeUp>
    <div className="mt-14 grid gap-6 lg:grid-cols-2">
      <FadeUp>
        <div data-testid="cg-before" className="h-full border border-slate-300 bg-white p-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Before</p>
          <div className="mt-6 space-y-0">
            {CREATIVE_BEFORE.map((step, i) => (
              <div key={step}>
                <p className="border border-slate-200 bg-paper px-4 py-3 text-sm text-slate-500">{step}</p>
                {i < CREATIVE_BEFORE.length - 1 && (
                  <ArrowDown size={14} className="mx-auto my-1.5 text-slate-400" />
                )}
              </div>
            ))}
          </div>
        </div>
      </FadeUp>
      <FadeUp delay={0.12}>
        <div data-testid="cg-after" className="h-full border border-gold/50 bg-white p-8 shadow-[0_24px_60px_-24px_rgba(140,109,31,0.4)]">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-golddeep">After CollabIgnite™</p>
          <div className="mt-6 space-y-0">
            {CREATIVE_AFTER.map((step, i) => (
              <div key={step}>
                <p className="border border-gold/40 bg-cream px-4 py-3 text-sm font-medium text-ink">{step}</p>
                {i < CREATIVE_AFTER.length - 1 && (
                  <ArrowDown size={14} className="mx-auto my-1.5 text-golddeep" />
                )}
              </div>
            ))}
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

const ThreeG = () => (
  <section data-testid="cg-3g" className="border-y border-gold/25 bg-cream">
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
      <FadeUp>
        <SectionTag>The 3G System™</SectionTag>
        <h2 className="mt-4 max-w-3xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Who you know matters. What you do with those relationships matters more.
        </h2>
      </FadeUp>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {CREATIVE_3G.map((g, i) => (
          <FadeUp key={g.title} delay={i * 0.08}>
            <article data-testid={`cg-3g-${i}`} className="h-full border border-gold/30 bg-white p-8">
              <span className="font-serif text-5xl font-bold text-gold/50">{g.title[0]}</span>
              <h3 className="mt-4 font-serif text-2xl font-semibold text-ink">{g.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{g.body}</p>
            </article>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2}>
        <a href="#lead-form" onClick={scrollToForm} data-testid="cg-3g-cta" className="btn-gold mt-12">
          Build My 3G Growth Map <ArrowRight size={16} />
        </a>
      </FadeUp>
    </div>
  </section>
);

const RevenueMap = () => (
  <section data-testid="cg-revenue-map" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <FadeUp>
      <SectionTag>The Revenue Opportunity Map</SectionTag>
      <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
        One talent. Multiple revenue pathways.
      </h2>
      <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-600">
        Where appropriate, your creative asset may open many doors. These are potential
        pathways, not promises. The map gets built around what you actually have.
      </p>
    </FadeUp>
    <FadeUp delay={0.1}>
      <div className="mt-12 flex flex-wrap items-center gap-2.5" data-testid="cg-revenue-chain">
        {CREATIVE_REVENUE_MAP.map((step, i) => (
          <span key={step} className="flex items-center gap-2.5">
            <span
              className={`border px-4 py-2.5 font-serif text-base ${
                i === 0 || i === CREATIVE_REVENUE_MAP.length - 1
                  ? "border-golddeep/60 bg-gold/15 font-semibold text-ink"
                  : "border-gold/30 bg-white text-slate-700"
              }`}
            >
              {step}
            </span>
            {i < CREATIVE_REVENUE_MAP.length - 1 && <ChevronRight size={14} className="text-golddeep" />}
          </span>
        ))}
      </div>
    </FadeUp>
  </section>
);

const CreativeIP = () => (
  <section data-testid="cg-ip" className="border-y border-gold/25 bg-cream">
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
      <div className="grid items-start gap-14 lg:grid-cols-2">
        <FadeUp>
          <SectionTag>Creative IP</SectionTag>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Your creative work may be more valuable than you think.
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
            Your creative output can become more than a project. CollabIgnite™ helps you
            identify and organize the potential commercial opportunities around it.
          </p>
          <div className="mt-8 border-l-2 border-gold bg-white p-6">
            <p className="text-xs leading-relaxed text-slate-500" data-testid="cg-ip-disclaimer">
              CollabIgnite™ provides business strategy and development support. Legal
              protection, legal opinions, securities advice, accounting, tax advice, and
              other regulated professional services are provided by appropriately licensed
              professionals.
            </p>
          </div>
        </FadeUp>
        <FadeUp delay={0.12}>
          <div className="flex flex-wrap gap-2.5">
            {CREATIVE_IP_LIST.map((item) => (
              <span key={item} className="border border-gold/30 bg-white px-4 py-2.5 font-serif text-base text-ink">
                {item}
              </span>
            ))}
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const Offers = () => (
  <section data-testid="cg-offers" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <FadeUp>
      <SectionTag>Creative Growth Offers</SectionTag>
      <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
        Three ways in. One direction: up.
      </h2>
    </FadeUp>
    <div className="mt-14 grid gap-6 lg:grid-cols-3">
      {CREATIVE_OFFERS.map((o, i) => (
        <FadeUp key={o.name} delay={i * 0.08}>
          <article
            data-testid={`cg-offer-${i}`}
            className={`flex h-full flex-col border p-8 transition-all duration-500 hover:-translate-y-1 ${
              i === 1
                ? "border-golddeep/60 bg-white shadow-[0_24px_60px_-24px_rgba(140,109,31,0.45)]"
                : "border-gold/25 bg-white"
            }`}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-golddeep">{o.audience}</p>
            <h3 className="mt-3 font-serif text-2xl font-semibold text-ink">{o.name}™</h3>
            <p className="mt-3 font-serif text-lg italic text-slate-700">{o.promise}</p>
            <ul className="mt-6 flex-1 space-y-2.5">
              {o.focus.map((f) => (
                <li key={f} className="flex gap-2.5 text-sm text-slate-600">
                  <Check size={15} className="mt-0.5 shrink-0 text-golddeep" /> {f}
                </li>
              ))}
            </ul>
            <a
              href="#lead-form"
              onClick={scrollToForm}
              data-testid={`cg-offer-cta-${i}`}
              className={`${i === 1 ? "btn-gold" : "btn-ghost"} mt-8 w-full`}
            >
              {o.cta}
            </a>
          </article>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Difference = () => (
  <section data-testid="cg-difference" className="border-y border-gold/25 bg-cream">
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
      <FadeUp>
        <SectionTag>Why CollabIgnite™</SectionTag>
        <h2 className="mt-4 max-w-3xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          You don't need another person telling you to "network."
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600">
          You need someone who understands how relationships, strategy, business
          development, creative assets, technology, and opportunities connect. The
          objective is not advice. The objective is your next valuable opportunity, and
          the infrastructure to act on it.
        </p>
        <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-golddeep">
          Creativity · Business · Relationships · Opportunity · Execution
        </p>
      </FadeUp>
      <div className="mt-14 grid gap-px bg-gold/25 sm:grid-cols-2 lg:grid-cols-3">
        {CREATIVE_DIFFERENCE.map((d, i) => (
          <FadeUp key={d.title} delay={i * 0.05}>
            <article data-testid={`cg-difference-${i}`} className="h-full bg-cream p-8">
              <span className="font-mono text-xs text-golddeep/60">0{i + 1}</span>
              <h3 className="mt-3 font-serif text-xl font-semibold text-ink">{d.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{d.body}</p>
            </article>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Proof = () => (
  <section data-testid="cg-proof" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
    <FadeUp>
      <SectionTag>Word of Mouth</SectionTag>
      <h2 className="mt-4 max-w-3xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
        The real question isn't whether you have talent. It's what you build with it.
      </h2>
    </FadeUp>
    <div className="mt-14 grid gap-6 md:grid-cols-2">
      {TESTIMONIALS.slice(0, 2).map((t, i) => (
        <FadeUp key={t.name} delay={i * 0.07}>
          <figure data-testid={`cg-testimonial-${i}`} className="h-full border border-gold/25 bg-white p-8">
            <Quote size={22} className="text-gold" />
            <blockquote className="mt-5 font-serif text-lg italic leading-relaxed text-slate-700">
              "{t.quote}"
            </blockquote>
            <figcaption className="mt-6 border-t border-gold/20 pt-4">
              <p className="text-sm font-semibold text-ink">{t.name}</p>
              <p className="text-xs text-slate-500">{t.role}</p>
            </figcaption>
          </figure>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Qualify = () => (
  <section data-testid="cg-qualify" className="border-y border-gold/25 bg-cream">
    <div className="mx-auto max-w-4xl px-6 py-28 lg:px-10">
      <FadeUp>
        <SectionTag>Is This For You?</SectionTag>
        <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
          Check every box that sounds like you.
        </h2>
      </FadeUp>
      <FadeUp delay={0.1}>
        <ul className="mt-10 space-y-4">
          {CREATIVE_QUALIFY.map((q, i) => (
            <li key={q} data-testid={`cg-qualify-${i}`} className="flex gap-4 border border-gold/25 bg-white p-5 text-sm leading-relaxed text-slate-700">
              <Check size={18} className="mt-0.5 shrink-0 text-golddeep" /> {q}
            </li>
          ))}
        </ul>
      </FadeUp>
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="cg-final-cta" className="relative overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,162,39,0.22),transparent_65%)]" />
    <div className="relative mx-auto max-w-5xl px-6 py-32 text-center lg:px-10">
      <FadeUp>
        <h2 className="font-serif text-4xl font-bold tracking-tight text-ink sm:text-5xl lg:text-6xl">
          You built the talent.<br />
          You built the audience.<br />
          <span className="gold-text italic">Now build the enterprise.</span>
        </h2>
        <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-slate-600">
          Your next level may not require more talent. It may require better strategy,
          stronger relationships, better business infrastructure, and a plan for turning
          your creative assets into opportunities.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a href="#lead-form" onClick={scrollToForm} data-testid="cg-final-cta-primary" className="btn-gold">
            Build My Creative Growth Plan <ArrowRight size={16} />
          </a>
          <Link to="/book" data-testid="cg-final-cta-secondary" className="btn-ghost">
            Book a Strategy Conversation
          </Link>
        </div>
        <p className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
          Clarity | Focus | Action
        </p>
      </FadeUp>
    </div>
  </section>
);

const LeadCapture = () => (
  <section id="lead-form" data-testid="cg-lead-section" className="border-t border-gold/25 bg-cream">
    <div className="mx-auto max-w-4xl px-6 py-28 lg:px-10">
      <FadeUp>
        <SectionTag>Let's See What You're Building</SectionTag>
        <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
          Your growth profile
        </h2>
        <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-600">
          This lands directly with Roy. Expect a direct reply about your best first move.
        </p>
      </FadeUp>
      <FadeUp delay={0.1}>
        <div className="mt-10 border border-gold/30 bg-white p-8 sm:p-10">
          <CreativeLeadForm />
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function CreativeGrowth() {
  return (
    <main data-testid="creative-growth-page">
      <Hero />
      <Problem />
      <Assets />
      <System />
      <Who />
      <Build />
      <Transformation />
      <ThreeG />
      <RevenueMap />
      <CreativeIP />
      <Offers />
      <Difference />
      <Proof />
      <Qualify />
      <FinalCta />
      <LeadCapture />
    </main>
  );
}
